/* B2B interactions through the existing consented GA4 destination. No purchase emitter. */
(function (scope) {
  'use strict';
  const events = new Set(['b2b_page_viewed', 'b2b_country_selected', 'b2b_tier_selected',
    'b2b_duration_selected', 'b2b_quantity_changed', 'b2b_continue_clicked',
    'b2b_checkout_ready', 'b2b_checkout_failed', 'b2b_quote_changed',
    'b2b_language_changed', 'b2b_link_clicked']);
  const markets = ['AE', 'KW', 'QA'];
  const tiers = ['BASIC', 'PREMIUM', 'STANDARD'];
  const durations = ['1M', '6M', '12M'];
  const reasons = ['stock', 'price', 'priceChanged', 'request', 'pending', 'conflict',
    'country', 'quantity', 'unavailable', 'timeout', 'unknown'];
  const links = ['header_logo', 'new_releases', 'must_watch', 'forever_favorites',
    'recently_added', 'binge_watch', 'only_on_yango_play', 'made_in_egypt',
    'best_selections', 'yasmina', 'music', 'activate_promo_code', 'contact_support',
    'agreement', 'facebook', 'instagram', 'tiktok'];
  const tier = value => typeof value === 'string' ? value.toUpperCase() : '';
  const duration = value => typeof value === 'number' ? `${value}M` : typeof value === 'string' ? value.toUpperCase() : '';
  const integer = (value, min, max) => Number.isSafeInteger(value) && value >= min && value <= max;

  function create({ context = () => ({}), enabled = () => true } = {}) {
    let viewed = false, disposed = false, pageRequested = false, readinessTimer = null, readinessAttempts = 0;
    const pendingHandoffs = new Set();
    const allowed = () => {
      try {
        const privacy = scope.Shopify?.customerPrivacy;
        return !disposed && enabled() === true && scope.IS_PROD === true && scope.YPGtmLoaded === true
          && typeof scope.gtag === 'function'
          && ['getyangoplay.com', 'www.getyangoplay.com'].includes(scope.location.hostname)
          && /^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?pages\/b2b\/?$/.test(scope.location.pathname)
          && !new URLSearchParams(scope.location.search).has('preview_theme_id')
          && privacy?.analyticsProcessingAllowed() === true && privacy?.marketingAllowed() === true;
      } catch { return false; }
    };
    const payload = extra => {
      const source = context() || {}, result = {
        send_to: 'G-JPLRNG5182', schema_version: 'b2b_v1', order_channel: 'b2b',
        page_surface: 'b2b', page_path: scope.location.pathname,
        page_location: scope.location.origin + scope.location.pathname,
      };
      if (['en', 'ar'].includes(source.locale)) result.locale = source.locale;
      if (markets.includes(source.market)) result.market = source.market;
      if (typeof source.country_confirmed === 'boolean') result.country_confirmed = source.country_confirmed;
      if (tiers.includes(tier(source.tier))) result.tier = tier(source.tier);
      if (durations.includes(duration(source.duration))) result.duration = duration(source.duration);
      if (/^[1-9]\d{0,19}$/.test(String(source.variant_id || ''))) result.variant_id = String(source.variant_id);
      if (integer(source.quantity, 1, 10000)) result.quantity = source.quantity;
      // All current B2B catalog contexts charge AED. Never infer currency from country.
      if (source.currency === 'AED') {
        result.currency = source.currency;
        for (const key of ['subtotal', 'discount_amount', 'total']) {
          if (integer(source[key], 0, Number.MAX_SAFE_INTEGER)) result[key] = source[key] / 100;
        }
      }
      if (Number.isFinite(source.discount_percent) && source.discount_percent >= 0 && source.discount_percent <= 100) result.discount_percent = source.discount_percent;
      for (const key of ['from_market', 'to_market']) if (markets.includes(extra[key])) result[key] = extra[key];
      for (const key of ['from_locale', 'to_locale']) if (['en', 'ar'].includes(extra[key])) result[key] = extra[key];
      for (const key of ['from_tier', 'to_tier']) if (tiers.includes(tier(extra[key]))) result[key] = tier(extra[key]);
      for (const key of ['from_duration', 'to_duration']) if (durations.includes(duration(extra[key]))) result[key] = duration(extra[key]);
      if (integer(extra.previous_quantity, 1, 10000)) result.previous_quantity = extra.previous_quantity;
      if (['stepper', 'input'].includes(extra.input_method)) result.input_method = extra.input_method;
      if (integer(extra.attempt, 1, 10000)) result.attempt = extra.attempt;
      if (typeof extra.is_retry === 'boolean') result.is_retry = extra.is_retry;
      if (reasons.includes(extra.reason)) result.reason = extra.reason;
      if (links.includes(extra.link_id)) result.link_id = extra.link_id;
      return result;
    };
    const track = (event, extra = {}) => {
      if (!events.has(event) || !allowed()) return false;
      try {
        scope.gtag('event', event, payload(extra || {}));
        return true;
      } catch { return false; /* Tracking must never interrupt commerce. */ }
    };
    const handoff = (extra = {}, onTracked) => {
      let completed = false, fallback = null;
      const finish = () => {
        if (completed) return;
        completed = true;
        if (fallback !== null) scope.clearTimeout(fallback);
        pendingHandoffs.delete(finish);
        if (typeof onTracked === 'function') onTracked();
      };
      if (!allowed()) { finish(); return false; }
      pendingHandoffs.add(finish);
      try {
        // Install our own bounded fallback before calling a third-party queue.
        // These transport controls are not analytical event properties.
        fallback = scope.setTimeout(finish, 600);
        scope.gtag('event', 'b2b_checkout_ready', {
          ...payload(extra || {}), event_callback: finish, event_timeout: 600,
        });
        return true;
      } catch { finish(); return false; }
    };
    const stopReadinessTimer = () => {
      if (readinessTimer !== null) scope.clearTimeout(readinessTimer);
      readinessTimer = null;
    };
    const checkPage = () => {
      stopReadinessTimer();
      if (!pageRequested || viewed || disposed) return;
      // This observes the current usable page, never replays earlier interactions.
      if (scope.document?.visibilityState !== 'hidden' && track('b2b_page_viewed')) { viewed = true; return; }
      if (++readinessAttempts < 60) readinessTimer = scope.setTimeout(checkPage, 500);
    };
    const pageViewed = () => { pageRequested = true; checkPage(); };
    const recheck = () => { readinessAttempts = 0; checkPage(); };
    scope.document?.addEventListener('visitorConsentCollected', recheck);
    scope.document?.addEventListener('visibilitychange', recheck);
    scope.addEventListener?.('load', recheck);
    const destroy = () => {
      disposed = true; stopReadinessTimer();
      scope.document?.removeEventListener('visitorConsentCollected', recheck);
      scope.document?.removeEventListener('visibilitychange', recheck);
      scope.removeEventListener?.('load', recheck);
      pendingHandoffs.forEach(finish => finish());
    };
    return Object.freeze({ track, handoff, pageViewed, destroy });
  }
  scope.YPB2BAnalytics = Object.freeze({ create });
})(typeof window === 'undefined' ? globalThis : window);
